import type {
  Conversation,
  ConversationParticipant,
  UserSummary,
} from "@/lib/api/messages";

/**
 * THE ROSTER NOW INCLUDES YOU. Legacy `participants` deliberately excluded the
 * current user; the v1 roster is the whole seat list with `isSelf` on the
 * viewer's own row (a conversation is a room, and the room contains you). So
 * every screen that reads "who am I talking to" filters first — forgetting to
 * would put the viewer's own name in the title of every conversation.
 */
export function otherParticipants(
  participants: ConversationParticipant[],
): UserSummary[] {
  return participants.filter((p) => !p.isSelf).map((p) => p.user);
}

/** A person's label. `name` is server-rendered; there is no username to fall back to (O-21). */
export function personName(user: UserSummary | null | undefined): string {
  return user?.name?.trim() || "Ukendt";
}

/**
 * The human label for a conversation's PARTICIPANTS. This is the fallback
 * `conversationTitle` falls back TO, and it is what a thread is called until
 * somebody names it.
 */
export function participantNames(
  users: UserSummary[],
  opts?: { firstNameOnly?: boolean },
): string {
  if (users.length === 0) return "Ingen deltagere";
  return users
    .map((user) =>
      opts?.firstNameOnly ? firstName(personName(user)) : personName(user),
    )
    .join(", ");
}

/**
 * WHAT TO CALL A CONVERSATION, everywhere one is labelled.
 *
 * A group thread may be given a NAME by any of its participants (OSO-442), and
 * when it has one that name is the title. When it does not — the normal case,
 * and every 1:1 — the participants are the title, exactly as they always were.
 *
 * THE NAME IS THE ONLY TITLE ON THIS SURFACE THAT MAY BE RENDERED, and the
 * distinction is worth stating because the repo's standing rule is the
 * opposite: a Discourse post or topic title is a UUID and must never reach the
 * screen. `Conversation.name` is not one of those. It is `null` unless a human
 * typed it, and the server stores no placeholder in its place — so this
 * function cannot put a UUID on a header, whatever the row holds.
 *
 * The server also guarantees a name is never blank (it trims, and stores an
 * empty result as `null`); the `.trim()` here is belt-and-braces so an
 * optimistic client-side value cannot render an empty header either.
 */
export function conversationTitle(
  conversation: Pick<Conversation, "name" | "participants">,
  opts?: { firstNameOnly?: boolean },
): string {
  const named = conversation.name?.trim();
  if (named) return named;
  return participantNames(otherParticipants(conversation.participants), opts);
}

/** First whitespace-separated token of a display name, for compact headers. */
export function firstName(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] || displayName;
}
